import { cn } from "@scribenote/brian-design-system";
import { useMemo } from "react";

import {
  type ClinicMapLive,
  displaysById,
  type LiveOccupant,
} from "./clinicMapClient";
import { MapCanvas } from "./MapCanvas";
import { drawnFrameOf, labelBox } from "./mapGeometry";
import { spaceName } from "./mapWords";

const TONE: Record<LiveOccupant["role"], string> = {
  staff: "bg-wash-working text-ink-working",
  client: "border-rule bg-card text-ink",
  animal: "bg-wash-brian text-ink-brian",
  unknown: "bg-recess text-annotation",
};

const UNNAMED: Record<LiveOccupant["role"], string> = {
  staff: "Staff",
  client: "Client",
  animal: "Animal",
  unknown: "Person",
};

const Chip = ({
  occupant,
  wall,
}: {
  occupant: LiveOccupant;
  wall: boolean;
}) => (
  <span
    className={cn(
      "rounded-paper border border-transparent leading-tight whitespace-nowrap",
      wall ? "px-2 py-0.5 text-base" : "px-1.5 py-0.5 text-xs",
      occupant.probable
        ? "bg-wash-flag text-ink-flag"
        : TONE[occupant.name ? occupant.role : "unknown"],
    )}
  >
    {occupant.name ?? UNNAMED[occupant.role]}
    {occupant.probable && " ?"}
  </span>
);

/** Everyone with no role, name or visit, as one count (BRI-337). */
const UnidentifiedChip = ({
  count,
  wall,
}: {
  count: number;
  wall: boolean;
}) => (
  <span
    className={cn(
      "rounded-paper border border-transparent leading-tight whitespace-nowrap",
      wall ? "px-2 py-0.5 text-base" : "px-1.5 py-0.5 text-xs",
      TONE.unknown,
    )}
  >
    {count} unidentified
  </span>
);

const share = (value: number, of: number) => `${(value / of) * 100}%`;

/**
 * The saved map with who each room's camera has on its roster now (BRI-269).
 * Names are brian's (`clinic_map/live.py`): staff by name, a client by given
 * name and surname initial, a patient by name; `?` while a candidate. A room
 * whose lane stopped writing is dimmed. A space no camera sees (the hallway, a
 * room whose camera sends no stills) also shows who walked into it in the last
 * minute and was not seen since (`areas`, BRI-266). Placed screens show
 * whether they are online.
 */
export const LiveClinicMap = ({
  live,
  wall = false,
  maxHeight,
  className,
}: {
  live: ClinicMapLive;
  /** Sized for a wall display across the room. */
  wall?: boolean;
  /** A CSS length; the map narrows to keep its height under it. */
  maxHeight?: string;
  className?: string;
}) => {
  const doc = live.document;
  const displays = useMemo(() => displaysById(live.displays), [live.displays]);
  const frame = drawnFrameOf(doc);
  const rooms = new Map(live.rooms.map((room) => [room.room_uuid, room]));
  const areas = new Map(
    live.areas.map((area) => [area.space_uuid, area.occupants]),
  );
  return (
    // `self-start`: the labels are placed in percent of this box, so a flex
    // parent must not stretch it taller than the map.
    <div
      className={cn("relative self-start", className)}
      style={
        maxHeight
          ? { maxWidth: `calc((${maxHeight}) * ${frame.width / frame.height})` }
          : undefined
      }
      data-dd-privacy="hidden"
    >
      <MapCanvas
        doc={doc}
        rooms={rooms}
        labelled={false}
        frame={frame}
        displays={displays}
      />
      {doc.spaces.map((space) => {
        const box = labelBox(space);
        const room = space.room_uuid ? rooms.get(space.room_uuid) : undefined;
        const occupants = [
          ...(room?.occupants ?? []),
          ...(areas.get(space.uuid) ?? []),
        ];
        const known = occupants.filter((o) => o.identified);
        const unidentified = occupants.length - known.length;
        return (
          <div
            key={space.uuid}
            className="pointer-events-none absolute flex flex-col items-center justify-center gap-1 overflow-hidden p-1 text-center"
            style={{
              left: share(box.x - frame.x, frame.width),
              top: share(box.y - frame.y, frame.height),
              width: share(box.w, frame.width),
              height: share(box.h, frame.height),
            }}
          >
            <span
              className={cn(
                "font-display text-ink",
                wall ? "text-xl" : "text-sm",
                !space.room_uuid && "text-annotation",
              )}
            >
              {spaceName(space, rooms)}
            </span>
            {occupants.length > 0 && (
              <div
                className={cn(
                  "flex flex-wrap justify-center gap-1",
                  room?.stale && "opacity-50",
                )}
              >
                {known.map((occupant, index) => (
                  <Chip key={index} occupant={occupant} wall={wall} />
                ))}
                {unidentified > 0 && (
                  <UnidentifiedChip count={unidentified} wall={wall} />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
